import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import RbFooter from "./components/RbFooter";

/* =====================================================================
   Responsible gambling
   =====================================================================
   The side menu and the footer both link here, so the links have to lead
   somewhere real.

   Deliberately generic on the legal side: it names no regulator, licence
   or helpline, because those are jurisdiction-specific and have to be
   filled in with whatever actually applies where this operates. The
   placeholders are marked in the source.
   ===================================================================== */

const SIGNS = [
  "Betting more than you planned, or more than you can afford to lose",
  "Chasing losses with bigger or more frequent bets",
  "Borrowing money, or selling things, to keep betting",
  "Betting to escape stress, boredom or low mood",
  "Hiding how much you bet from people close to you",
  "Betting getting in the way of work, study, sleep or relationships",
];

const TOOLS = [
  {
    icon: "fa-sliders",
    title: "Deposit limits",
    body: "Cap how much you can add to your account per day, week or month. Contact support to set one on your account.",
  },
  {
    icon: "fa-clock",
    title: "Take a break",
    body: "Pause your account for a fixed period. During a break you cannot deposit or place bets.",
  },
  {
    icon: "fa-ban",
    title: "Self-exclusion",
    body: "Close your account for a longer period. Self-exclusion cannot be reversed before the period you choose has ended.",
  },
  {
    icon: "fa-file-lines",
    title: "Your activity",
    body: "Review every bet, deposit and withdrawal on your account from the Transactions and Profit & Loss pages.",
  },
];

const ResponsibleGaming = () => {
  const navigate = useNavigate();
  const [isSidebar, setSidebar] = useState(false);
  const isDesktop = window.innerWidth > 991;

  return (
    <>
      <Header isSidebar={isSidebar} setSidebar={setSidebar} />
      <div className="body-container" style={{ display: "flex" }}>
        <Sidebar isSidebar={isSidebar} setSidebar={setSidebar} isDesktop={isDesktop} />
        <div className="rightbar" style={{ width: "100%", minWidth: 0 }}>
          <div className="rb-shell">
            <section className="rb-section">
              <div className="rb-section__head">
                <h1 className="rb-section__title" style={{ fontSize: "24px" }}>
                  Responsible gambling
                </h1>
              </div>
              <p style={{ color: "var(--rb-text-2)", maxWidth: "62ch", lineHeight: 1.65 }}>
                Betting should be entertainment you can afford, not a way to
                make money. This page explains the limits you can put on your
                own account, how to spot when betting has stopped being fun,
                and where to get help.
              </p>
            </section>

            <section className="rb-section">
              <div className="rb-section__head">
                <h2 className="rb-section__title">Age restriction</h2>
              </div>
              <div className="rb-footer__rg" style={{ marginTop: 0 }}>
                <span className="rb-footer__age" aria-hidden="true">
                  18+
                </span>
                <p className="rb-footer__rg-text">
                  <strong>You must be at least 18.</strong> You must also be old
                  enough to gamble legally where you live, and live somewhere
                  that allows residents to gamble online. Accounts opened by
                  anyone under age are closed and any winnings are void.
                </p>
              </div>
            </section>

            <section className="rb-section">
              <div className="rb-section__head">
                <h2 className="rb-section__title">Tools you can use</h2>
              </div>
              <div className="rb-trust" style={{ marginTop: 0 }}>
                {TOOLS.map((t) => (
                  <div className="rb-trust__card" key={t.title}>
                    <span className="rb-trust__icon" aria-hidden="true">
                      <i className={"fa " + t.icon} />
                    </span>
                    <span>
                      <span className="rb-trust__title">{t.title}</span>
                      <span className="rb-trust__body">{t.body}</span>
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="rb-section">
              <div className="rb-section__head">
                <h2 className="rb-section__title">Signs to watch for</h2>
              </div>
              <ul
                style={{
                  color: "var(--rb-text-2)",
                  lineHeight: 1.8,
                  maxWidth: "62ch",
                  paddingLeft: "20px",
                }}
              >
                {SIGNS.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
              <p style={{ color: "var(--rb-text-2)", maxWidth: "62ch", lineHeight: 1.65 }}>
                If several of these sound familiar, set a limit or take a break
                today, and talk to someone about it.
              </p>
            </section>

            <section className="rb-section">
              <div className="rb-section__head">
                <h2 className="rb-section__title">Getting help</h2>
              </div>
              {/* TODO(operator): replace with the support organisations and
                  helpline numbers for the jurisdictions this operates in.
                  Left generic on purpose -- naming the wrong regulator or a
                  helpline that does not serve the user's country is worse
                  than naming none. */}
              <p style={{ color: "var(--rb-text-2)", maxWidth: "62ch", lineHeight: 1.65 }}>
                Free, confidential support for gambling problems is available in
                most countries. Search for the gambling support service in your
                country, or speak to a doctor or counsellor.
              </p>
              <div className="rb-section__head" style={{ marginTop: "24px" }}>
                <button
                  type="button"
                  className="rb-btn rb-btn--primary"
                  onClick={() => navigate("/contact-us")}
                >
                  Contact our support team
                </button>
              </div>
            </section>
          </div>

          <RbFooter />
        </div>
      </div>
    </>
  );
};

export default ResponsibleGaming;
